/**
 * Custom Explorer view -- user-defined X vs Y exploration.
 * Auto-adapts visualization: scatter for continuous X, strip plot for categorical X.
 *
 * FR-60/61: X/Y axis dropdowns
 * FR-62: Auto-adapt visualization by vizType
 * FR-63: Strip plot with jitter for categorical X
 * FR-64: Trend line for scatter
 * FR-65: Color by Verdict / Media Type / dimension
 */

import { useState, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Label,
} from 'recharts';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SkeletonCard } from '@/components/dashboard/shared/SkeletonLoaders';
import { useExplorer } from '@/hooks/useDashboard';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import { META_KPI_CATALOG } from '@/types/dashboard';
import type { ExplorerPoint } from '@/types/dashboard';
import type { Template } from '@/types/template';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

const MEDIA_TYPE_COLORS: Record<string, string> = {
  video: '#2563FF',
  image: '#F59E0B',
};

type ColorByMode = 'verdict' | 'mediaType';

// ============================================================================
// HELPERS
// ============================================================================

function addJitter(value: number, range = 0.03): number {
  return value + (Math.random() - 0.5) * 2 * range * Math.max(value, 1);
}

// Flat jitter for integer rating scales -- reveals overplotting on tight
// domains (e.g. 1-5) where proportional jitter would be invisible.
function jitterScale(value: number): number {
  return value + (Math.random() - 0.5) * 0.3;
}

interface ScaleConfig {
  min: number;
  max: number;
  ticks: number[];
}

// Returns scale-axis config for a diagnosis dimension, or null for anything
// else (KPIs, gatePassRate, options/binary dimensions).
function resolveScaleConfig(
  template: Template | null | undefined,
  axisId: string,
): ScaleConfig | null {
  const dim = template?.diagnosisDimensions?.find((d) => d.dimensionId === axisId);
  if (!dim || dim.outputConfig.type !== 'scale') return null;
  const min = dim.outputConfig.scaleMin ?? 1;
  const max = dim.outputConfig.scaleMax ?? 5;
  const ticks = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return { min, max, ticks };
}

// Format an axis value for display: integers for scale axes, 2dp otherwise.
function formatAxisValue(value: unknown, scaleCfg: ScaleConfig | null): string {
  if (typeof value !== 'number') return String(value ?? '');
  return scaleCfg ? value.toFixed(0) : value.toFixed(2);
}

interface PlotDotProps {
  cx?: number;
  cy?: number;
  payload?: ExplorerPoint & { hasData: boolean };
  colorBy?: ColorByMode;
}

function PlotDot({ cx = 0, cy = 0, payload, colorBy = 'verdict' }: PlotDotProps) {
  if (!payload) return null;
  const color = colorBy === 'mediaType'
    ? MEDIA_TYPE_COLORS[payload.mediaType] ?? '#2563FF'
    : VERDICT_COLORS[payload.verdict] ?? '#2563FF';
  return (
    <circle
      cx={cx}
      cy={cy}
      r={7}
      fill={color}
      fillOpacity={payload.hasData ? 0.75 : 0.3}
      stroke={color}
      strokeWidth={payload.hasData ? 1 : 1.5}
      strokeDasharray={payload.hasData ? 'none' : '3 2'}
    />
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CustomExplorer() {
  const { filters, activeTemplate } = useDashboardContext();

  const primaryKpiId = activeTemplate?.kpiConfig?.primaryKpiId ?? null;

  // Build X-axis options from template dimensions + taxonomy + KPIs
  const xAxisOptions = useMemo(() => {
    const options: { id: string; label: string }[] = [];
    // Dimensions
    for (const dim of activeTemplate?.diagnosisDimensions ?? []) {
      if (dim.enabled) {
        options.push({ id: dim.dimensionId, label: dim.name });
      }
    }
    // KPIs
    for (const kpi of META_KPI_CATALOG) {
      options.push({ id: kpi.id, label: kpi.label });
    }
    options.push({ id: 'gatePassRate', label: 'Gate Pass Rate' });
    return options;
  }, [activeTemplate]);

  // Build Y-axis options: scale dimensions + all KPIs + gate pass rate.
  // Only scale-type dimensions are numerically plottable on Y; options-type
  // dimensions stay X-only (strip plot handles them there).
  const yAxisOptions = useMemo(() => {
    const options: { id: string; label: string }[] = [];
    for (const dim of activeTemplate?.diagnosisDimensions ?? []) {
      if (dim.enabled && dim.outputConfig.type === 'scale') {
        options.push({ id: dim.dimensionId, label: dim.name });
      }
    }
    for (const kpi of META_KPI_CATALOG) {
      options.push({
        id: kpi.id,
        label: `${kpi.label}${kpi.id === primaryKpiId ? ' * Primary' : ''}`,
      });
    }
    options.push({ id: 'gatePassRate', label: 'Gate Pass Rate (%)' });
    return options;
  }, [activeTemplate, primaryKpiId]);

  const defaultXId = xAxisOptions[0]?.id ?? 'gatePassRate';
  const defaultYId = primaryKpiId && primaryKpiId !== defaultXId
    ? primaryKpiId
    : yAxisOptions.find(o => o.id !== defaultXId)?.id ?? 'ctr';

  const [xAxisId, setXAxisId] = useState(defaultXId);
  const [yAxisId, setYAxisId] = useState(defaultYId);
  const [colorBy, setColorBy] = useState<ColorByMode>('verdict');

  const { data, isLoading, isError } = useExplorer(filters, xAxisId, yAxisId);

  const explorerData = data;
  const points = explorerData?.points ?? [];
  const vizType = explorerData?.vizType ?? 'scatter';

  // Labels come from the frontend's own option lists -- the backend returns
  // raw IDs (e.g. "dim_cepMentalAvailability"), but we already know their
  // display names from activeTemplate and META_KPI_CATALOG.
  const xLabel = xAxisOptions.find((o) => o.id === xAxisId)?.label ?? xAxisId;
  const yLabel = yAxisOptions.find((o) => o.id === yAxisId)?.label ?? yAxisId;

  const xScaleCfg = useMemo(
    () => resolveScaleConfig(activeTemplate, xAxisId),
    [activeTemplate, xAxisId],
  );
  const yScaleCfg = useMemo(
    () => resolveScaleConfig(activeTemplate, yAxisId),
    [activeTemplate, yAxisId],
  );

  // Chart points: preserve raw x/y for tooltip display, jitter scale axes
  // and strip-plot Y to reveal overplotting.
  const chartPoints = useMemo(() => {
    return points.map((p) => ({
      ...p,
      xRaw: p.x,
      yRaw: p.y,
      xVal: xScaleCfg && typeof p.x === 'number' ? jitterScale(p.x) : p.x,
      yVal:
        yScaleCfg && typeof p.y === 'number'
          ? jitterScale(p.y)
          : vizType === 'strip' && typeof p.y === 'number'
            ? addJitter(p.y)
            : p.y,
      hasData: p.y !== null && p.y !== undefined,
    }));
  }, [points, vizType, xScaleCfg, yScaleCfg]);

  // Y average for reference line
  const yAvg = useMemo(() => {
    const vals = chartPoints.filter((p) => typeof p.yVal === 'number').map((p) => p.yVal as number);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }, [chartPoints]);

  // Trend line for scatter (FR-64)
  const trendLine = useMemo(() => {
    if (vizType !== 'scatter') return null;
    const pts = chartPoints.filter(
      (p) => typeof p.xVal === 'number' && typeof p.yVal === 'number'
    ) as { xVal: number; yVal: number }[];
    if (pts.length < 3) return null;
    const n = pts.length;
    const sumX = pts.reduce((s, p) => s + p.xVal, 0);
    const sumY = pts.reduce((s, p) => s + p.yVal, 0);
    const sumXY = pts.reduce((s, p) => s + p.xVal * p.yVal, 0);
    const sumX2 = pts.reduce((s, p) => s + p.xVal * p.xVal, 0);
    const denom = n * sumX2 - sumX * sumX;
    if (denom === 0) return null;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    const minX = Math.min(...pts.map((p) => p.xVal));
    const maxX = Math.max(...pts.map((p) => p.xVal));
    return { slope, intercept, minX, maxX };
  }, [chartPoints, vizType]);

  if (isLoading) {
    return <SkeletonCard lines={8} />;
  }

  if (isError || !explorerData) {
    return (
      <EmptyState
        title="No axes available for exploration"
        description="Add diagnosis dimensions or connect Meta performance data to explore relationships."
      />
    );
  }

  const isScatter = vizType === 'scatter';

  // CSV export
  const csvHeaders = ['Creative', 'Verdict', 'X Value', 'Y Value', 'Media Type'];
  const csvRows = points.map((p) => [
    p.creativeName,
    p.verdict,
    p.x,
    p.y,
    p.mediaType,
  ]);

  return (
    <div className="rounded-[14px] bg-surface shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div>
          <div className="text-lg font-semibold text-primary">Custom Explorer</div>
          <div className="text-xs text-muted mt-0.5">Does X predict Y? Select axes to explore.</div>
        </div>
        <CsvExport headers={csvHeaders} rows={csvRows} filename="custom-explorer" />
      </div>

      {/* Axis selectors (FR-60/61) */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-border-subtle bg-surface-subtle">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-secondary">X-axis</label>
          <select
            value={xAxisId}
            onChange={(e) => setXAxisId(e.target.value)}
            className="text-xs border border-border-subtle rounded px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {xAxisOptions.map((ax) => (
              <option key={ax.id} value={ax.id}>{ax.label}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-muted">vs</span>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-secondary">Y-axis</label>
          <select
            value={yAxisId}
            onChange={(e) => setYAxisId(e.target.value)}
            className="text-xs border border-border-subtle rounded px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {yAxisOptions.map((ax) => (
              <option key={ax.id} value={ax.id}>{ax.label}</option>
            ))}
          </select>
        </div>

        {/* Color by selector (FR-65) */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-secondary">Color by</label>
          <select
            value={colorBy}
            onChange={(e) => setColorBy(e.target.value as ColorByMode)}
            className="text-xs border border-border-subtle rounded px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand"
          >
            <option value="verdict">Verdict</option>
            <option value="mediaType">Media Type</option>
          </select>
        </div>

        <span className="text-xs text-muted ml-2">
          {isScatter ? 'Scatter plot' : 'Strip plot'}
        </span>

        {/* Legend */}
        <div className="ml-auto flex items-center gap-3 text-xs text-muted">
          {colorBy === 'verdict' ? (
            <>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success" />Scale</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" />Fix</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-danger" />Kill</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand" />Video</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" />Image</span>
            </>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="px-4 py-4">
        {chartPoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-sm font-medium text-secondary">No data points available</div>
            <div className="text-xs text-muted mt-1 max-w-xs">
              No creatives have values for both selected axes. Try a different axis combination.
            </div>
          </div>
        ) : isScatter ? (
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7ECF6" strokeOpacity={0.5} />
              <XAxis
                type="number"
                dataKey="xVal"
                name={xLabel}
                tick={{ fontSize: 10, fill: '#8B95A7' }}
                domain={xScaleCfg ? [xScaleCfg.min - 0.25, xScaleCfg.max + 0.25] : ['auto', 'auto']}
                ticks={xScaleCfg?.ticks}
                allowDecimals={!xScaleCfg}
              >
                <Label value={xLabel} offset={-10} position="insideBottom" style={{ fontSize: 11, fill: '#8B95A7' }} />
              </XAxis>
              <YAxis
                type="number"
                dataKey="yVal"
                name={yLabel}
                tick={{ fontSize: 10, fill: '#8B95A7' }}
                width={45}
                axisLine={false}
                domain={yScaleCfg ? [yScaleCfg.min - 0.25, yScaleCfg.max + 0.25] : undefined}
                ticks={yScaleCfg?.ticks}
                allowDecimals={!yScaleCfg}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload as ExplorerPoint & { xRaw: number | string; yRaw: number };
                  if (!d) return null;
                  return (
                    <div className="rounded-[10px] bg-surface shadow-lg border border-border-subtle p-2 text-xs">
                      <div className="font-semibold text-primary">{d.creativeName}</div>
                      <div style={{ color: VERDICT_COLORS[d.verdict] }}>{d.verdict}</div>
                      <div className="mt-1">
                        <div>{xLabel}: {formatAxisValue(d.xRaw, xScaleCfg)}</div>
                        <div>{yLabel}: {formatAxisValue(d.yRaw, yScaleCfg)}</div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={yAvg} stroke="#8B95A7" strokeDasharray="4 4" />
              {trendLine && (
                <ReferenceLine
                  segment={[
                    { x: trendLine.minX, y: trendLine.slope * trendLine.minX + trendLine.intercept },
                    { x: trendLine.maxX, y: trendLine.slope * trendLine.maxX + trendLine.intercept },
                  ]}
                  stroke="#2563FF"
                  strokeWidth={1.5}
                />
              )}
              <Scatter data={chartPoints} shape={<PlotDot colorBy={colorBy} />} />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          /* Strip plot for categorical X (FR-63) */
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7ECF6" strokeOpacity={0.5} />
              <XAxis
                type="category"
                dataKey="xVal"
                name={xLabel}
                tick={{ fontSize: 10, fill: '#8B95A7' }}
                allowDuplicatedCategory={false}
              >
                <Label value={xLabel} offset={-10} position="insideBottom" style={{ fontSize: 11, fill: '#8B95A7' }} />
              </XAxis>
              <YAxis
                type="number"
                dataKey="yVal"
                name={yLabel}
                tick={{ fontSize: 10, fill: '#8B95A7' }}
                width={45}
                axisLine={false}
                domain={yScaleCfg ? [yScaleCfg.min - 0.25, yScaleCfg.max + 0.25] : undefined}
                ticks={yScaleCfg?.ticks}
                allowDecimals={!yScaleCfg}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload as ExplorerPoint & { xRaw: string; yRaw: number };
                  if (!d) return null;
                  return (
                    <div className="rounded-[10px] bg-surface shadow-lg border border-border-subtle p-2 text-xs">
                      <div className="font-semibold text-primary">{d.creativeName}</div>
                      <div style={{ color: VERDICT_COLORS[d.verdict] }}>{d.verdict}</div>
                      <div className="mt-1">
                        <div>{xLabel}: {d.xRaw}</div>
                        <div>{yLabel}: {formatAxisValue(d.yRaw, yScaleCfg)}</div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={yAvg} stroke="#8B95A7" strokeDasharray="4 4" />
              <Scatter data={chartPoints} shape={<PlotDot colorBy={colorBy} />} />
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
